import type { AnimationPort, AnimationRequest } from "../features/match/controller/types";

export type ActiveAnimation = Readonly<{
  id: number;
  request: AnimationRequest;
}>;

export type AnimationChannel = Readonly<{
  port: AnimationPort;
  getSnapshot(): ActiveAnimation | null;
  subscribe(listener: () => void): () => void;
  finish(id: number): void;
}>;

/**
 * Bridges the imperative controller port to a React renderer. Cancellation always resolves the
 * controller's wait and drops the visual request, so interruption cannot strand a locked phase.
 */
export function createAnimationChannel(): AnimationChannel {
  const listeners = new Set<() => void>();
  let active: ActiveAnimation | null = null;
  let nextId = 1;
  let resolveActive: (() => void) | null = null;
  let disposed = false;

  const publish = (): void => {
    for (const listener of [...listeners]) listener();
  };

  const finish = (id: number): void => {
    if (active?.id !== id) return;
    active = null;
    const resolve = resolveActive;
    resolveActive = null;
    publish();
    resolve?.();
  };

  const cancel = (): void => {
    if (active === null) return;
    finish(active.id);
  };

  const port: AnimationPort = Object.freeze({
    play(request) {
      if (disposed) return Promise.resolve();
      cancel();
      const id = nextId;
      nextId += 1;
      active = Object.freeze({ id, request });
      const completion = new Promise<void>((resolve) => {
        resolveActive = resolve;
      });
      publish();
      return completion;
    },
    cancel,
    dispose() {
      if (disposed) return;
      disposed = true;
      cancel();
      listeners.clear();
    },
  });

  return Object.freeze({
    port,
    getSnapshot: () => active,
    subscribe(listener) {
      if (disposed) return () => undefined;
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    finish,
  });
}
